<template>
  <div>
    <div class="w-full h-full bg-white">
      <div class="h-full flex flex-nowrap md:flex-wrap flex-col md:flex-row">
        <div
          :key="x"
          v-for="(grouped, x) in groupedServices"
          class="p-1.5 w-full md:w-1/3 h-full max-h-[16rem]"
        >
          <CollapsibleSection
            :header-label="$t(grouped[0].transactionsGroup)"
            :initial-expanded-state="false"
            body-class="!p-0"
          >
            <div class="px-4 py-3 max-h-[12rem] overflow-y-auto">
              <button
                :key="y"
                v-for="(item, y) in grouped"
                class="text-start flex justify-between gap-x-8 ltr:font-sans text-xs hover:text-primary"
                @click="onServiceClick(item)"
              >
                {{ item.serviceFullDescription }}
                <faChevronRight class="w-3 fill-primary" />
              </button>
            </div>
          </CollapsibleSection>
        </div>
      </div>
    </div>
    <ModalVue v-model="confirmModal" :title="$t('goTo')" container-class="w-32rem">
      <template #bodyContent>
        <p class="p-3">
          {{ $t('sponsoredOrCanceledFilesOrVisaVisitingRelativesAndFriends') }}
        </p>
      </template>
      <template #footerContent>
        <div class="w-full flex justify-end gap-x-1">
          <RouterLink
            :to="{
              name: 'ServiceForm',
              params: {
                serviceId: confirmModal.serviceTransId
              },
              query: buildServiceFormQuery(confirmModal, { isCanceled: 0 })
            }"
            class="px-2.5 py-1 text-center rounded-md bg-primary/80 hover:bg-primary flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white"
          >
            {{ $t('Sponsored') }}
          </RouterLink>
          <RouterLink
            :to="{
              name: 'ServiceForm',
              params: {
                serviceId: confirmModal.serviceTransId
              },
              query: buildServiceFormQuery(confirmModal, { isCanceled: 1 })
            }"
            class="px-2.5 py-1 text-center rounded-md bg-primary/80 hover:bg-primary flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white"
          >
            {{ $t('CanceledFiles') }}
          </RouterLink>
          <RouterLink
            :to="{
              name: 'ServiceForm',
              params: {
                serviceId: confirmModal.serviceTransId
              },
              query: buildServiceFormQuery(confirmModal)
            }"
            class="px-2.5 py-1 text-center rounded-md bg-primary/80 hover:bg-primary flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white"
          >
            {{ $t('visasForVisitingRelativesAndFriends') }}
          </RouterLink>
          <button
            class="px-2.5 py-1 text-center rounded-md border border-brand-gray-600 hover:border-primary/80 flex items-center gap-x-2 active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-brand-gray-600 hover:text-primary"
            @click="confirmModal = false"
          >
            {{ $t('cancel') }}
          </button>
        </div>
      </template>
    </ModalVue>
  </div>
</template>

<script setup>
import CollapsibleSection from '@/components/Ui/CollapsibleSection.vue'
import FavoriteButton from '@/components/Buttons/Service/FavoriteButton.vue'
import OpenServicecardButton from '@/components/Buttons/Service/OpenServicecard.vue'
import StartButton from '@/components/Buttons/Service/StartButton.vue'
import ServiceCardVue from '@/components/cards/ServiceCard.vue'
import axiosInstance from '@/services'
import { useFavoriteService } from '@/stores/favorite_services.js'
import { onMounted, ref, computed } from 'vue'
import { DateTime } from 'luxon'
import { useAuthStore } from '@/stores/auth'
import client_lookups from '@/assets/json/clientLookups.json'
import _ from 'lodash'
import faChevronRight from '@/assets/icons/faChevronRight.svg?component'
import { useRouter } from 'vue-router'
import { useGlobalStore } from '@/stores/global'
import ModalVue from '@/components/Ui/Modal/index.vue'

const router = useRouter()
const favoriteServicesStore = useFavoriteService()
const authStore = useAuthStore()
const globalStore = useGlobalStore()

const {
  adminRegions: { dubai },
  module: { ICA, familyBook },
  ServiceTransactions: { updateHomeAndWorkAddressInformation }
} = client_lookups

const groupedServices = ref([])
const confirmModal = ref(false)

onMounted(async () => {
  const services = await axiosInstance.get('/api/dashboard/latestReleasedService')
  // thirty days ago
  const thirdyDA = DateTime.now().minus({ days: 30 })

  const serviceIds = []
  services.forEach((module) => {
    module.moduleServices.forEach((parentService) => {
      parentService.childs.forEach((subService) => {
        const creationDateLxn = DateTime.fromISO(subService.serviceTransactions[0].creationDate)
        if (creationDateLxn <= thirdyDA) return

        serviceIds.push({
          serviceTransactionId: subService.serviceTransactions[0].id,
          creationDate: subService.serviceTransactions[0].creationDate,
          creationDateLxn
        })
      })
    })
  })

  const nameDetails = await axiosInstance.get('/api/service/nameDetails', {
    params: { emirateId: null }
  })

  services.value = nameDetails.filter((serviceItem) => {
    return serviceIds.some((si) => serviceItem.serviceTransId == si.serviceTransactionId)
  })

  const hasDubaiSponsorDept = authStore.individualInfo.sponsorFileDepartments.some(
    (sfd) => sfd.immigrationDepartmentId == dubai
  )
  if (hasDubaiSponsorDept) {
    services.value = services.value.filter((f) => [8, ICA, familyBook].includes(f.moduleId))
  }

  groupedServices.value = _.groupBy(services.value, 'transactionsGroupId')
})

function buildServiceFormQuery(service, extraQuery = {}) {
  const { moduleId, lookupAdminRegionId } = service
  return {
    moduleId,
    adminRegionId: lookupAdminRegionId,
    withException: false,
    ...extraQuery
  }
}

async function onServiceClick(service) {
  const { serviceTransId } = service

  if (serviceTransId == updateHomeAndWorkAddressInformation) {
    // TODO: router.push({name: StartUpdatePersonAddressRequest})
  }

  const serviceConfig = await globalStore.fetchConfig(serviceTransId, {
    administrativeRegionId: service.lookupAdminRegionId
  })

  if (!serviceConfig) return

  const { requiredEmployee, requiredFamilyBook, requiredNonCitizenWife, showFinesPopup } =
    serviceConfig
  const isAnyTrue = [requiredEmployee, requiredFamilyBook, requiredNonCitizenWife].some((x) => !!x)
  if (isAnyTrue) {
    router.push({
      name: 'ServiceForm',
      params: {
        serviceId: serviceTransId
      },
      query: {
        ...buildServiceFormQuery(service)
      }
    })
  }

  if (showFinesPopup) {
    confirmModal.value = service
  }
}
</script>

<style lang="scss" scoped></style>
